/**
 * Catalyst 2026 - Main JavaScript Bundle
 * Compiled with Webpack
 */

// Import libraries
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade, Thumbs } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

// Make Swiper available globally for modules
window.Swiper = Swiper;
window.SwiperModules = { Navigation, Pagination, Autoplay, EffectFade, Thumbs };

// Import module-specific scripts
import './blocks/header.js';
import { initHeaderSearch } from './blocks/header_search.js';
import './blocks/gsap_animations.js';
import './blocks/icons_slider.js';
import './blocks/history_slider.js';
import './blocks/images_row.js';
import './blocks/accordion.js';
import './blocks/product_feature_grid.js';
import './blocks/testimonials_slider.js';
import './blocks/card_carousel.js';
import './blocks/circle_tickers.js';
import './blocks/cta_cards.js';
import './blocks/content_slider.js';

// Global utilities
document.addEventListener('DOMContentLoaded', function() {
  
  // Initialize header search
  initHeaderSearch();
  
  // Smooth scroll for anchor links
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      const href = this.getAttribute('href');
      if (href === '#') return;
      
      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    });
  });

  // Mobile menu toggle
  const menuToggle = document.querySelector('.mobile-menu-toggle');
  const mobileMenu = document.querySelector('.mobile-menu');
  
  if (menuToggle && mobileMenu) {
    menuToggle.addEventListener('click', () => {
      mobileMenu.classList.toggle('active');
      menuToggle.classList.toggle('active');
    });
  }

  // Lazy load images
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          observer.unobserve(img);
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }

  // Force autoplay on background videos — iOS Safari ignores the autoplay
  // attribute and requires an explicit .play() call even with muted + playsinline.
  document.querySelectorAll('video[autoplay]').forEach(video => {
    const attempt = () => video.play().catch(() => {});
    attempt();
    document.addEventListener('touchstart', attempt, { once: true });
  });

  // Add animation classes on scroll
  const animateObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
      }
    });
  }, {
    threshold: 0.1
  });

  document.querySelectorAll('.module-animate, .module-animate-fade, .module-animate-slide-left, .module-animate-slide-right').forEach(el => {
    animateObserver.observe(el);
  });

  // Initialize logos slider
  document.querySelectorAll('.logos-slider').forEach(slider => {
    const track = slider.querySelector('.logos-track');
    if (!track) return;

    const slides = Array.from(track.children);
    const uniqueCount = Math.round(slides.length / 2);
    const GAP = 32;
    let current = 0;
    let offset = 0;
    let animating = false;
    let resizeTimer;

    function advance() {
      if (animating) return;
      animating = true;
      offset += slides[current].offsetWidth + GAP;
      current++;
      track.style.transition = 'transform 0.6s ease';
      track.style.transform = `translateX(-${offset}px)`;
      setTimeout(() => {
        if (current >= uniqueCount) {
          track.style.transition = 'none';
          current = 0;
          offset = 0;
          track.style.transform = 'translateX(0)';
        }
        animating = false;
      }, 650);
    }

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        current = 0;
        offset = 0;
        track.style.transition = 'none';
        track.style.transform = 'translateX(0)';
      }, 150);
    });

    setInterval(advance, 4000);
  });
  
  // Module Overlap Functionality
  function resetModuleOverlap() {
    // Reset all overlap modules to original state
    const overlapModules = document.querySelectorAll('.module-wrapper.overlap-previous');
    overlapModules.forEach(module => {
      if (module.dataset.originalMarginTop !== undefined) {
        module.style.marginTop = module.dataset.originalMarginTop;
        delete module.dataset.originalMarginTop;
      } else {
        module.style.marginTop = '';
      }

      const previousModule = module.previousElementSibling;
      if (previousModule && previousModule.classList.contains('module-wrapper')) {
        if (previousModule.dataset.overlapOriginalPaddingBottom !== undefined) {
          previousModule.style.paddingBottom = previousModule.dataset.overlapOriginalPaddingBottom;
          delete previousModule.dataset.overlapOriginalPaddingBottom;
        } else {
          previousModule.style.paddingBottom = '';
        }
      }

      module.classList.remove('overlap-calculated', 'overlap-applied');
    });
  }
  
  function handleModuleOverlap() {
    // Reset and skip on mobile
    resetModuleOverlap();
    if (window.innerWidth < 768) return;
    
    const overlapModules = document.querySelectorAll('.module-wrapper.overlap-previous');
    
    overlapModules.forEach(module => {
      // Find the previous module
      const previousModule = module.previousElementSibling;
      
      if (previousModule && previousModule.classList.contains('module-wrapper')) {
        if (module.dataset.originalMarginTop === undefined) {
          module.dataset.originalMarginTop = module.style.marginTop || '';
        }

        if (previousModule.dataset.overlapOriginalPaddingBottom === undefined) {
          previousModule.dataset.overlapOriginalPaddingBottom = previousModule.style.paddingBottom || '';
        }

        // Get overlap percentage from data attribute
        const overlapPercentage = parseInt(module.dataset.overlapPercentage) || 30;
        
        // Ensure module has height (wait for images to load if needed)
        const moduleHeight = module.offsetHeight;
        
        // Skip if module has no height (might be hidden or loading)
        if (moduleHeight === 0) {
          console.log('Module has no height, skipping overlap calculation');
          return;
        }
        
        // Calculate overlap amount based on module height and percentage
        const overlapAmount = Math.round(moduleHeight * (overlapPercentage / 100));
        
        // Set CSS custom property for pseudo element positioning
        module.style.setProperty('--overlap-percentage', `${overlapPercentage}%`);
        
        // Apply negative margin to current module
        module.style.marginTop = `-${overlapAmount}px`;
        
        // Add extra padding to previous module
        const currentPadding = parseInt(window.getComputedStyle(previousModule).paddingBottom) || 0;
        previousModule.style.paddingBottom = `${currentPadding + overlapAmount}px`;
        
        // Mark as calculated and applied
        module.classList.add('overlap-calculated');
        module.classList.add('overlap-applied');

        // Debug logging
        console.log(`Overlap applied: ${overlapPercentage}% of ${moduleHeight}px = ${overlapAmount}px`);
      }
    });

    // Recalculate ScrollTrigger positions after layout shift
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  }
  
  // Debounced resize handler
  let resizeTimeout;
  function debouncedHandleModuleOverlap() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(handleModuleOverlap, 150);
  }
  
  // Run on page load (with delay for images) and resize
  setTimeout(handleModuleOverlap, 100); // Wait 100ms for images to load
  window.addEventListener('resize', debouncedHandleModuleOverlap);
  
  // Also run when all images are loaded
  window.addEventListener('load', handleModuleOverlap);
});
